import React, {useState, Dispatch, SetStateAction} from 'react';
import {useCommand, useSelection} from 'hooks';
import {useTranslation} from 'react-i18next';
import {
	DefaultButton,
	Dialog,
	DialogFooter,
	DialogType,
	Label,
	mergeStyleSets,
	Modal,
	Panel,
	PanelType,
	PrimaryButton,
	SelectionMode,
	Separator,
	Stack,
} from '@fluentui/react';
import {useForm} from 'react-hook-form';
import {Requirement, VehicleProject, UserRole} from 'types';
import {LoadWrapper} from 'components/LoadWrapper';
import {EntityList, EntityListColumn} from 'components';
import {
	useGetAddRequirementsFormDataLazyQuery,
	useGetAddRequirementsFormDataQuery,
} from 'features/RegulatoryDocuments/hooks/useGetAddRequirementsFormData.generated';
import {
	getArrayFieldColumn,
	getRequirementStatusColumn,
	getTextColumn,
} from 'helpers';
import FieldForReasonToAddRequirements from './FieldForReasonToAddRequirements';
import {RequirementsTooltipTranslationProvider} from 'features/Requirements/RequirementsTooltipTranslationProvider';
import {useAssignVehicleProjectRequirementsMutation} from 'features/VehicleProjects/hooks';
import {root} from 'index';
import {useRequirementColumns} from 'features/Requirements/hooks/useRequirementColumns';

export type SetAddReason = Dispatch<SetStateAction<string>>;

interface Props {
	modelSeries: string;
	generation: string;
	refreshData: () => void; // Add this line
}

export const AssignVehicleProjectRequirementsForm = ({
	modelSeries,
	generation,
	refreshData,
}: Props) => {
	const {t} = useTranslation('features/vehicleprojects');
	const [isHidden, setHidden] = React.useState<boolean>(true);

	type StateForAddReason = [string, SetAddReason];

	/**
	 * This shouldn't be undefined because it means the text field will switch from
	 * being an uncontrolled to a controlled component.
	 */
	const [reasonToAddRequirements, setAddReason]: StateForAddReason =
		useState<string>('');

	const [getRequirementsFormData, {data: dataForm, loading: loadingForm}] =
		useGetAddRequirementsFormDataLazyQuery();

	const requirementData = React.useMemo(
		() => dataForm?.requirements ?? [],
		[dataForm],
	);

	const [items, setItems] = React.useState(requirementData);

	const [selectedRequirements, setSelectedRequirements] = React.useState<
		Requirement[]
	>([]);

	const [selection] = useSelection<Requirement>({
		onSelectionChanged(selectedItems) {
			setSelectedRequirements(selectedItems);
		},
		getKey: item => item.id,
	});

	const [assignRequirementsBatch] =
		useAssignVehicleProjectRequirementsMutation();

	useCommand(
		{
			text: t('AssignRequirements'),
			key: 'assignRequirements',
			iconProps: {
				iconName: 'Add',
			},
			title: t('AssignRequirements'),
			priority: 2,
			onClick() {
				setHidden(false);
				if (!loadingForm && !dataForm) {
					getRequirementsFormData();
				}
			},
			roles: [UserRole.SystemAdministrator, UserRole.Bedarfskoordinator],
		},
		[dataForm, loadingForm],
	);

	const closeAndReset = (): void => {
		setHidden(true);
		selection.setAllSelected(false);
		setAddReason('');
	};

	const handleCancelClick = React.useCallback(() => {
		closeAndReset();
		setItems(requirementData);
	}, [requirementData, selection]);

	const {handleSubmit} = useForm<VehicleProject>({
		reValidateMode: 'onSubmit',
		mode: 'all',
	});

	const handleSaveClick = React.useCallback(() => {
		handleSubmit(() => {
			assignRequirementsBatch({
				variables: {
					input: {
						modelSeries,
						generation,
						requirementIds: selectedRequirements.map(r => r.id),
						addReason: reasonToAddRequirements,
					},
				},
				refetchQueries: [],
			}).then(() => {
				refreshData();
			});
		})();

		closeAndReset();
	}, [
		modelSeries,
		generation,
		selectedRequirements,
		selection,
		reasonToAddRequirements,
	]);

	const columns = useRequirementColumns().slice(1);

	const onRenderBody = () => (
		<div style={{margin: 25}}>
			<LoadWrapper loading={loadingForm}>
				<FieldForReasonToAddRequirements
					value={reasonToAddRequirements}
					setValue={setAddReason}
					selectedRequirements={selectedRequirements}
				/>
				<RequirementsTooltipTranslationProvider>
					<EntityList
						items={items.length > 0 ? items : requirementData}
						columns={columns}
						aria-rowcount
						selectionMode={SelectionMode.multiple}
						selection={selection}
					/>
				</RequirementsTooltipTranslationProvider>
			</LoadWrapper>
		</div>
	);

	const onRenderFooterContent: any = React.useCallback(
		() => (
			<form>
				<PrimaryButton
					styles={{root: {marginRight: 8}}}
					onClick={handleSaveClick}
					disabled={!reasonToAddRequirements}
				>
					{t('AssignButton')}
				</PrimaryButton>
				<DefaultButton onClick={handleCancelClick}>
					{t('CancelButton')}
				</DefaultButton>
			</form>
		),
		[handleSaveClick, handleCancelClick, reasonToAddRequirements],
	);

	return (
		<Panel
			isOpen={!isHidden}
			onDismiss={handleCancelClick}
			headerText={t('AssignRequirements')}
			onRenderFooterContent={onRenderFooterContent}
			isFooterAtBottom={true}
			type={PanelType.smallFluid}
			onRenderBody={onRenderBody}
		></Panel>
	);
};
